/* @jsx h */
import { useQuery } from "@tanstack/vue-query";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useMenu, useTRPC } from "@app/vue/providers/index";
import { computed, defineComponent, h, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

type WorkflowState = {
  status:
    | "initializing"
    | "waiting_for_payment"
    | "waiting_for_confirmation"
    | "payment_confirmed"
    | "completed"
    | "failed"
    | "timeout";
  paymentId?: number;
  providerId?: string;
  paymentUrl?: string;
  redirectPath?: string;
  error?: string;
};

type StatusTone = "loading" | "success" | "error";

export const ShopCheckoutPaymentStatusPage = defineComponent({
  name: "ShopCheckoutPaymentStatusPage",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const trpc = useTRPC();
    const { shop } = useMenu();
    const { t } = useLocalization();

    const shopSlug = computed(() => route.params.shopslug as string);
    const workflowId = computed(() => route.params.checkoutId as string);
    const returnedFromProvider = computed(
      () => route.query.returned === "true",
    );
    const providerFlags = computed(() => ({
      success: route.query.success === "true",
      failed: route.query.failed === "true",
      cancel: route.query.cancel === "true",
    }));

    const error = ref<string | null>(null);

    const { data: workflowData, isPending } = useQuery({
      queryKey: ["integrated-customer-payment-workflow", workflowId],
      enabled: computed(() => Boolean(workflowId.value)),
      refetchInterval: (query) => {
        const state = query.state.data?.output;
        if (
          !state ||
          state.status === "initializing" ||
          state.status === "waiting_for_payment" ||
          state.status === "waiting_for_confirmation" ||
          state.status === "payment_confirmed"
        ) {
          return 2000;
        }

        return false;
      },
      retry: false,
      queryFn: async () =>
        trpc.integratedCustomerPaymentWorkflow.getStatus.query({
          workflowId: workflowId.value,
        }),
    });

    const workflowState = computed(
      () =>
        (workflowData.value?.output || {
          status: "initializing",
        }) as WorkflowState,
    );

    watch(
      workflowData,
      (data) => {
        const state = data?.output as WorkflowState | undefined;
        if (!state) return;

        if (
          state.status === "waiting_for_payment" &&
          state.paymentUrl &&
          !returnedFromProvider.value
        ) {
          window.location.href = state.paymentUrl;
          return;
        }

        if (state.status === "completed" && state.redirectPath) {
          router.push(state.redirectPath);
          return;
        }

        if (state.status === "failed") {
          error.value =
            state.error || t("customer.payment_status.payment_failed");
          return;
        }

        if (state.status === "timeout") {
          error.value = t("customer.payment_status.payment_timeout_copy");
          return;
        }

        error.value = null;
      },
      { immediate: true },
    );

    const statusMeta = computed(() => {
      if (error.value) {
        return {
          tone: "error" as StatusTone,
          title: t("customer.payment_status.payment_error"),
          message: error.value,
          icon: "close",
        };
      }

      if (isPending.value && !workflowData.value) {
        return {
          tone: "loading" as StatusTone,
          title: t("customer.payment_status.loading"),
          message: t("customer.payment_status.checking_status"),
          icon: "hourglass_top",
        };
      }

      switch (workflowState.value.status) {
        case "initializing":
          return {
            tone: "loading" as StatusTone,
            title: t("customer.payment_status.initializing"),
            message: t("customer.payment_status.setting_up_payment"),
            icon: "credit_card",
          };
        case "waiting_for_payment":
          return {
            tone: "loading" as StatusTone,
            title: returnedFromProvider.value
              ? t("customer.payment_status.checking_payment")
              : t("customer.payment_status.redirecting"),
            message: returnedFromProvider.value
              ? t("customer.payment_status.verifying_provider")
              : t("customer.payment_status.taking_to_payment_page"),
            icon: returnedFromProvider.value ? "sync" : "north_east",
          };
        case "waiting_for_confirmation":
          return {
            tone: "loading" as StatusTone,
            title: t("customer.payment_status.confirming_payment"),
            message: t("customer.payment_status.waiting_provider"),
            icon: "schedule",
          };
        case "payment_confirmed":
          return {
            tone: "success" as StatusTone,
            title: t("customer.payment_status.payment_confirmed"),
            message: t("customer.payment_status.processing_checkout"),
            icon: "check_circle",
          };
        case "completed":
          return {
            tone: "success" as StatusTone,
            title: t("customer.payment_status.success"),
            message: t("customer.payment_status.finalizing_checkout"),
            icon: "task_alt",
          };
        case "failed":
          return {
            tone: "error" as StatusTone,
            title: t("customer.payment_status.payment_failed"),
            message:
              workflowState.value.error ||
              t("customer.payment_status.something_went_wrong"),
            icon: "error",
          };
        case "timeout":
          return {
            tone: "error" as StatusTone,
            title: t("customer.payment_status.payment_timeout"),
            message: t("customer.payment_status.payment_timeout_copy"),
            icon: "schedule",
          };
        default:
          return {
            tone: "loading" as StatusTone,
            title: t("customer.payment_status.processing"),
            message: t("customer.payment_status.please_wait"),
            icon: "hourglass_top",
          };
      }
    });

    const providerNotice = computed(() => {
      if (!returnedFromProvider.value) return null;

      if (providerFlags.value.cancel) {
        return t("customer.payment_status.provider_cancel_notice");
      }

      if (providerFlags.value.failed) {
        return t("customer.payment_status.provider_failed_notice");
      }

      if (providerFlags.value.success) {
        return t("customer.payment_status.provider_success_notice");
      }

      return t("customer.payment_status.provider_return_notice");
    });

    const progressItems = computed(() => [
      {
        label: t("customer.payment_status.payment_page"),
        done:
          workflowState.value.status !== "initializing" &&
          workflowState.value.status !== "waiting_for_payment",
      },
      {
        label: t("customer.payment_status.provider_confirmation"),
        done:
          workflowState.value.status === "payment_confirmed" ||
          workflowState.value.status === "completed",
      },
      {
        label: t("customer.payment_status.checkout_complete"),
        done: workflowState.value.status === "completed",
      },
    ]);

    const backToRestaurant = () => {
      router.push(`/restaurant/${shopSlug.value}/home`);
    };

    return () => {
      const status = statusMeta.value;

      return (
        <div class="flex min-h-screen items-center justify-center p-4 sm:p-6">
          <div class="w-full max-w-2xl">
            <div class="card bg-base-100 shadow-xl border">
              <div class="card-body gap-6">
                <div class="text-center">
                  <p class="text-sm text-base-content/60">
                    {shop.value?.name || t("customer.payment_status.checkout")}
                  </p>
                  <h1 class="text-2xl font-semibold mt-2">
                    {t("customer.payment_status.title")}
                  </h1>
                  <p class="text-sm text-base-content/60 mt-2">
                    {t("customer.payment_status.reference", [workflowId.value])}
                  </p>
                </div>

                {providerNotice.value && (
                  <div class="alert">
                    <span>{providerNotice.value}</span>
                  </div>
                )}

                <div class="border rounded-xl p-6 text-center">
                  {status.tone === "loading" && (
                    <div class="flex flex-col items-center gap-4">
                      <Loading />
                      <div>
                        <div class="text-xl font-semibold">{status.title}</div>
                        <div class="text-sm text-base-content/60 mt-2">
                          {status.message}
                        </div>
                      </div>
                    </div>
                  )}

                  {status.tone === "success" && (
                    <div class="flex flex-col items-center gap-4">
                      <span class="material-symbols-outlined text-5xl text-success">
                        {status.icon}
                      </span>
                      <div>
                        <div class="text-xl font-semibold">{status.title}</div>
                        <div class="text-sm text-base-content/60 mt-2">
                          {status.message}
                        </div>
                      </div>
                    </div>
                  )}

                  {status.tone === "error" && (
                    <div class="flex flex-col items-center gap-4">
                      <span class="material-symbols-outlined text-5xl text-error">
                        {status.icon}
                      </span>
                      <div>
                        <div class="text-xl font-semibold">{status.title}</div>
                        <div class="text-sm text-base-content/60 mt-2">
                          {status.message}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div class="border rounded-xl p-4">
                  <h2 class="font-medium mb-3">
                    {t("customer.payment_status.progress")}
                  </h2>
                  <div class="space-y-3">
                    {progressItems.value.map((item) => (
                      <div
                        key={item.label}
                        class="flex items-center justify-between text-sm"
                      >
                        <span>{item.label}</span>
                        <span
                          class={`material-symbols-outlined text-lg ${
                            item.done ? "text-success" : "text-base-content/40"
                          }`}
                        >
                          {item.done
                            ? "check_circle"
                            : "radio_button_unchecked"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div class="flex flex-col gap-3 sm:flex-row sm:justify-center hidden">
                  <button class="btn btn-primary" onClick={backToRestaurant}>
                    {t("customer.payment_status.back_to_menu")}
                  </button>
                  {(status.tone === "error" || providerFlags.value.cancel) && (
                    <button class="btn btn-ghost" onClick={() => router.go(0)}>
                      {t("customer.payment_status.refresh_status")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };
  },
});
